<script setup>
import dayjs from 'dayjs'
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { Dialog } from 'quasar'
import { getI18n } from '@/plugins/i18n'

import { useChatStore } from '@/stores/chat.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useChips } from '@/composables/chips'

import ListItem from '@/components/shared/ListItem.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import ChatHistoryTitle from '@/components/chat/ChatHistoryTitle.vue'

const i18n = getI18n()
const chatStore = useChatStore()
const webSiteStore = useWebSiteStore()
const { archivedChip } = useChips()

const conversationToDelete = ref(null)

let pollTimer = null
let isPolling = true

const pollConversationsAsync = async() => {
  await chatStore.fetchConversationsAsync()
  if (isPolling && chatStore.conversations.some(conversation => conversation.is_running)) {
    pollTimer = setTimeout(pollConversationsAsync, webSiteStore.getChatHistoryPollInterval())
  }
}

onMounted(pollConversationsAsync)
onBeforeUnmount(() => {
  isPolling = false
  clearTimeout(pollTimer)
})

const onToggleArchived = async(value) => {
  chatStore.showArchivedConversations = value
  await chatStore.fetchConversationsAsync()
}

const onRename = (conversation) => {
  Dialog.create({
    title: i18n.t('chat.renameConversation'),
    prompt: { model: conversation.title, type: 'text', isValid: value => !!value.trim() },
    cancel: { flat: true, label: i18n.t('common.cancel') },
    ok: { color: 'primary', label: i18n.t('common.confirm') },
  }).onOk(title => chatStore.renameConversationAsync(conversation, title.trim()))
}

const onDeleteConfirmed = async() => {
  await chatStore.deleteConversationAsync(conversationToDelete.value.id)
  conversationToDelete.value = null
}
</script>

<template>
  <div class="column no-wrap full-height">
    <div class="row items-center no-wrap q-px-md q-py-xs">
      <div class="col text-subtitle2 text-grey-8">{{ i18n.t('chat.history') }}</div>
      <q-toggle
        dense
        size="sm"
        :model-value="chatStore.showArchivedConversations"
        :label="i18n.t('common.showArchived')"
        @update:model-value="onToggleArchived"
      />
    </div>
    <q-scroll-area class="col">
      <div v-if="!chatStore.conversations.length" class="q-pa-lg text-center text-grey-7">
        {{ i18n.t('chat.noConversations') }}
      </div>
      <q-list class="q-px-sm">
        <q-item
          v-for="conversation in chatStore.conversations"
          :key="conversation.id"
          v-ripple
          clickable
          class="menu-entry chat-history__entry"
          :class="{ 'menu-entry--active': conversation.id === chatStore.conversationId }"
          @click="chatStore.openConversationAsync(conversation.id)"
        >
          <list-item
            dense
            class="full-width q-pa-none"
            :caption="dayjs(conversation.updated_at).format('L LT')"
            :caption-chips="conversation.archived ? [archivedChip] : []"
          >
            <template #label>
              <chat-history-title :title="conversation.title" />
            </template>
            <template #avatar>
              <q-avatar>
                <q-spinner-ball v-if="conversation.is_running" color="primary" size="sm" />
                <q-icon v-else name="mdi-chat-outline" :color="conversation.is_unread ? 'primary' : 'grey-5'" />
                <q-tooltip v-if="conversation.is_running || conversation.is_unread">
                  {{ i18n.t(conversation.is_running ? 'chat.answering' : 'chat.newAnswer') }}
                </q-tooltip>
              </q-avatar>
            </template>
            <template #right>
              <div class="row no-wrap items-center">
                <q-btn
                  v-if="!conversation.archived"
                  flat
                  dense
                  size="sm"
                  color="primary"
                  :title="i18n.t('chat.renameConversation')"
                  @click.stop="onRename(conversation)"
                >
                  <i class="dx-icon dx-icon-edit chat-history__grid-icon" />
                </q-btn>
                <q-btn
                  v-if="!conversation.archived"
                  flat
                  dense
                  size="sm"
                  color="primary"
                  :title="i18n.t('chat.deleteConversation')"
                  @click.stop="conversationToDelete = conversation"
                >
                  <i class="dx-icon dx-icon-trash chat-history__grid-icon" />
                </q-btn>
                <q-btn
                  flat
                  dense
                  size="sm"
                  color="primary"
                  :title="i18n.t(conversation.archived ? 'common.unarchive' : 'common.archive')"
                  :icon="conversation.archived ? 'mdi-archive-arrow-up' : 'mdi-archive'"
                  @click.stop="chatStore.archiveConversationAsync(conversation)"
                />
              </div>
            </template>
          </list-item>
        </q-item>
      </q-list>
    </q-scroll-area>

    <confirm-dialog
      :model-value="conversationToDelete !== null"
      :message="i18n.t('common.confirmDelete')"
      icon="mdi-delete-outline"
      icon-color="negative"
      @update:model-value="conversationToDelete = $event ? conversationToDelete : null"
      @confirm="onDeleteConfirmed"
    />
  </div>
</template>

<style scoped lang="scss">
.chat-history__entry {
  margin: 0.15rem 0;
  padding: 0.4rem 0.5rem;
}

.chat-history__grid-icon {
  font-size: 1.125rem;
}
</style>
